import { webglTileRenderer } from '../terrain/webgl-tile-renderer.js'
import { DEM_TILE_SIZE } from '../terrain/dem-config.js'

const PROGRAM_KEY = 'los-coverage-v4'
const TILE_SIZE = DEM_TILE_SIZE
const PADDED_SIZE = TILE_SIZE * 3
const DEG2RAD = Math.PI / 180

const HORIZON_GRID_SIZE = 128
const NUM_AZIMUTHS = 72
const AZIMUTH_STEP = (2 * Math.PI) / NUM_AZIMUTHS
const HORIZON_TRACE_STEPS = 80

// RGBA-packed horizon texture layout:
// 4 azimuths per texel → 18 texels per cell
// 64 cells per row → texture width 1152, height 256
const AZIMUTHS_PER_TEXEL = 4
const TEXELS_PER_CELL = NUM_AZIMUTHS / AZIMUTHS_PER_TEXEL // 18
const CELLS_PER_ROW = 64
const HORIZON_TEX_WIDTH = CELLS_PER_ROW * TEXELS_PER_CELL // 1152
const HORIZON_TEX_HEIGHT = Math.ceil((HORIZON_GRID_SIZE * HORIZON_GRID_SIZE) / CELLS_PER_ROW) // 256

const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_texCoord;

uniform sampler2D u_sats;
uniform float u_satCount;
uniform float u_satTexHeight;
uniform vec4 u_boundsRad;
uniform float u_minElevation;

uniform sampler2D u_horizon;
uniform float u_gridSize;

const float EARTH_R = 6378.137;
const float E2 = 0.00669437999014;
const float TWO_PI = 6.28318530718;
const float HALF_PI = 1.57079632679;
const float NUM_AZ = 72.0;
const float CELLS_PER_ROW = 64.0;
const float TEXELS_PER_CELL = 18.0;
const float TEX_W = 1152.0;
const float TEX_H = 256.0;

float ch4(vec4 t, float c) {
  if (c < 0.5) return t.r;
  if (c < 1.5) return t.g;
  if (c < 2.5) return t.b;
  return t.a;
}

float getHorizonAngle(float azimuth) {
  float a = mod(azimuth, TWO_PI);
  if (a < 0.0) a += TWO_PI;
  float azBin = a / TWO_PI * NUM_AZ;
  float ai0 = floor(azBin);
  float ai1 = mod(ai0 + 1.0, NUM_AZ);
  float frac = azBin - ai0;

  float cellCol = min(floor(v_texCoord.x * u_gridSize), u_gridSize - 1.0);
  float cellRow = min(floor(v_texCoord.y * u_gridSize), u_gridSize - 1.0);
  float ci = cellRow * u_gridSize + cellCol;

  float texRow = floor(ci / CELLS_PER_ROW);
  float posInRow = ci - texRow * CELLS_PER_ROW;
  float hv = (texRow + 0.5) / TEX_H;

  float tg0 = floor(ai0 / 4.0);
  float c0 = ai0 - tg0 * 4.0;
  float hu0 = (posInRow * TEXELS_PER_CELL + tg0 + 0.5) / TEX_W;
  float angle0 = ch4(texture2D(u_horizon, vec2(hu0, hv)), c0) * HALF_PI;

  float tg1 = floor(ai1 / 4.0);
  float c1 = ai1 - tg1 * 4.0;
  float hu1 = (posInRow * TEXELS_PER_CELL + tg1 + 0.5) / TEX_W;
  float angle1 = ch4(texture2D(u_horizon, vec2(hu1, hv)), c1) * HALF_PI;

  return mix(angle0, angle1, frac);
}

void main() {
  float lonRad = mix(u_boundsRad.x, u_boundsRad.y, v_texCoord.x);
  float latRad = mix(u_boundsRad.w, u_boundsRad.z, v_texCoord.y);

  float sinLat = sin(latRad);
  float cosLat = cos(latRad);
  float sinLon = sin(lonRad);
  float cosLon = cos(lonRad);

  float N = EARTH_R / sqrt(1.0 - E2 * sinLat * sinLat);
  float obsX = N * cosLat * cosLon;
  float obsY = N * cosLat * sinLon;
  float obsZ = N * (1.0 - E2) * sinLat;

  for (int i = 0; i < 4096; i++) {
    if (float(i) >= u_satCount) break;

    float row = (float(i) + 0.5) / u_satTexHeight;
    vec4 p0 = texture2D(u_sats, vec2(0.25, row));
    vec4 p1 = texture2D(u_sats, vec2(0.75, row));

    float satX = p0.r * 255.0 * 256.0 + p0.g * 255.0 - 32768.0;
    float satY = p0.b * 255.0 * 256.0 + p0.a * 255.0 - 32768.0;
    float satZ = p1.r * 255.0 * 256.0 + p1.g * 255.0 - 32768.0;

    float dx = satX - obsX;
    float dy = satY - obsY;
    float dz = satZ - obsZ;

    float south = sinLat * cosLon * dx + sinLat * sinLon * dy - cosLat * dz;
    float east = -sinLon * dx + cosLon * dy;
    float up = cosLat * cosLon * dx + cosLat * sinLon * dy + sinLat * dz;

    float elevation = atan(up, sqrt(south * south + east * east));

    if (elevation >= u_minElevation) {
      float azimuth = atan(east, -south);
      float horizonAngle = getHorizonAngle(azimuth);
      if (elevation > horizonAngle) {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
        return;
      }
    }
  }

  gl_FragColor = vec4(220.0/255.0, 40.0/255.0, 40.0/255.0, 1.0);
}
`

function tileToLng(x: number, z: number): number {
  return (x / Math.pow(2, z)) * 360 - 180
}

function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

function decodeTerrariumElevation(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768
}

export function computeHorizonGrid(
  paddedDem: Uint8ClampedArray,
  metersPerPixel: number
): { packed: Uint8ClampedArray; width: number; height: number } {
  const packed = new Uint8ClampedArray(HORIZON_TEX_WIDTH * HORIZON_TEX_HEIGHT * 4)
  const cellPixelSize = TILE_SIZE / HORIZON_GRID_SIZE

  for (let row = 0; row < HORIZON_GRID_SIZE; row++) {
    for (let col = 0; col < HORIZON_GRID_SIZE; col++) {
      const ci = row * HORIZON_GRID_SIZE + col

      const demPx = TILE_SIZE + (col + 0.5) * cellPixelSize
      const demPy = TILE_SIZE + (row + 0.5) * cellPixelSize

      const cx = Math.max(0, Math.min(PADDED_SIZE - 1, Math.round(demPx)))
      const cy = Math.max(0, Math.min(PADDED_SIZE - 1, Math.round(demPy)))
      const obsIdx = (cy * PADDED_SIZE + cx) * 4
      const obsElev = decodeTerrariumElevation(
        paddedDem[obsIdx],
        paddedDem[obsIdx + 1],
        paddedDem[obsIdx + 2]
      )

      const texRow = Math.floor(ci / CELLS_PER_ROW)
      const posInRow = ci % CELLS_PER_ROW

      for (let ai = 0; ai < NUM_AZIMUTHS; ai++) {
        const azimuth = ai * AZIMUTH_STEP
        const dx = Math.sin(azimuth)
        const dy = -Math.cos(azimuth)
        let maxAngle = 0

        for (let step = 2; step <= HORIZON_TRACE_STEPS; step += 2) {
          const sx = Math.round(demPx + dx * step)
          const sy = Math.round(demPy + dy * step)
          if (sx < 0 || sx >= PADDED_SIZE || sy < 0 || sy >= PADDED_SIZE) break

          const sIdx = (sy * PADDED_SIZE + sx) * 4
          const elev = decodeTerrariumElevation(
            paddedDem[sIdx],
            paddedDem[sIdx + 1],
            paddedDem[sIdx + 2]
          )
          const angle = Math.atan2(elev - obsElev, step * metersPerPixel)
          if (angle > maxAngle) maxAngle = angle
        }

        const encoded = Math.round(Math.min(1, maxAngle / (Math.PI / 2)) * 255)
        const texelGroup = Math.floor(ai / AZIMUTHS_PER_TEXEL)
        const channel = ai % AZIMUTHS_PER_TEXEL
        const texX = posInRow * TEXELS_PER_CELL + texelGroup
        const texIdx = (texRow * HORIZON_TEX_WIDTH + texX) * 4
        packed[texIdx + channel] = encoded
      }
    }
  }

  return { packed, width: HORIZON_TEX_WIDTH, height: HORIZON_TEX_HEIGHT }
}

export function packSatPositions(
  ecfData: Float32Array,
  errData: Int8Array,
  satCount: number,
  timeCount: number,
  timeIndex: number
): { packed: Uint8ClampedArray; width: number; height: number; validCount: number } {
  let validCount = 0
  for (let si = 0; si < satCount; si++) {
    if (errData[si * timeCount + timeIndex] === 0) validCount++
  }
  if (validCount === 0) {
    return { packed: new Uint8ClampedArray(0), width: 0, height: 0, validCount: 0 }
  }

  const width = 2
  const height = validCount
  const packed = new Uint8ClampedArray(width * height * 4)

  let vi = 0
  for (let si = 0; si < satCount; si++) {
    if (errData[si * timeCount + timeIndex] !== 0) continue
    const base = (si * timeCount + timeIndex) * 3
    const x = ecfData[base]
    const y = ecfData[base + 1]
    const z = ecfData[base + 2]

    const ex = Math.round(x + 32768)
    const ey = Math.round(y + 32768)
    const ez = Math.round(z + 32768)

    const row = vi * width * 4
    packed[row] = (ex >> 8) & 0xff
    packed[row + 1] = ex & 0xff
    packed[row + 2] = (ey >> 8) & 0xff
    packed[row + 3] = ey & 0xff
    packed[row + 4] = (ez >> 8) & 0xff
    packed[row + 5] = ez & 0xff
    packed[row + 6] = 0
    packed[row + 7] = 0
    vi++
  }

  return { packed, width, height, validCount }
}

export function renderLosTileGPU(
  satTexture: WebGLTexture,
  satCount: number,
  satTexHeight: number,
  horizonTexture: WebGLTexture,
  z: number,
  x: number,
  y: number,
  minElevationRad: number
): Uint8ClampedArray | null {
  if (!webglTileRenderer.available && !webglTileRenderer.init()) return null

  const program = webglTileRenderer.compileProgram(FRAGMENT_SRC, PROGRAM_KEY)
  if (!program) return null

  const minLng = tileToLng(x, z) * DEG2RAD
  const maxLng = tileToLng(x + 1, z) * DEG2RAD
  const maxLat = tileToLat(y, z) * DEG2RAD
  const minLat = tileToLat(y + 1, z) * DEG2RAD

  return webglTileRenderer.renderToPixels(
    program,
    {
      u_satCount: satCount,
      u_satTexHeight: satTexHeight,
      u_boundsRad: [minLng, maxLng, minLat, maxLat],
      u_minElevation: minElevationRad,
      u_gridSize: HORIZON_GRID_SIZE,
    },
    {
      u_sats: { texture: satTexture, unit: 0 },
      u_horizon: { texture: horizonTexture, unit: 1 },
    },
    TILE_SIZE,
    TILE_SIZE
  )
}
